import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaPlus, FaSignOutAlt, FaBriefcase, FaEdit, FaTrash, FaRobot } from 'react-icons/fa';
import { signOutRedirect } from '../../auth/cognitoLogout';
import { useApiWithAuth } from '../../auth/apiHelper';
import styles from './adminInsights.module.scss';

type BlogFormData = {
    title: string;
    description: string;
    category: string;
    body: string;
    imageKey: string;
    active: boolean;
}

type Blog = BlogFormData & {
    postID: string;
    pubDate: string;
};

type AiArticleResponse = {
    postID?: string;
    pubDate?: string;
    title?: string;
    description?: string;
    body?: string;
    category?: string;
    imageKey?: string;
    active?: boolean;
};

const AdminInsightsPage = () => {
    const navigate = useNavigate();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');
    const [errorMessage, setErrorMessage] = useState('');
    const [blogs, setBlogs] = useState<Blog[]>([]);
    const [blogsLoading, setBlogsLoading] = useState(false);
    const [editingId, setEditingId] = useState<string | null>(null);
    const [imageFile, setImageFile] = useState<File | null>(null);
    const [editingPubDate, setEditingPubDate] = useState<string | null>(null);
    const [deletingId, setDeletingId] = useState<string | null>(null);
    const [aiTopic, setAiTopic] = useState('');
    const [aiCategory, setAiCategory] = useState('Technology');
    const [aiCustomPrompt, setAiCustomPrompt] = useState('');
    const [aiGenerating, setAiGenerating] = useState(false);
    const [aiMessage, setAiMessage] = useState('');
    const [bodyFile, setBodyFile] = useState<File | null>(null);

    const fetchWithAuth = useApiWithAuth();

    const imageInputRef = useRef<HTMLInputElement>(null);
    const bodyInputRef = useRef<HTMLInputElement>(null);

    const toBase64 = (file: File): Promise<string> =>
    new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
            const result = reader.result as string;
            resolve(result.split(',')[1]); // strip "data:image/jpeg;base64,"
        };
        reader.onerror = reject;
        reader.readAsDataURL(file);
    });

    const extractTextFromFile = async (file: File): Promise<string> => {
        const ext = file.name.split('.').pop()?.toLowerCase() || '';

        if (ext === 'txt' || ext === 'md' || ext === 'text') {
            return await file.text();
        }

        if (ext === 'docx') {
            try {
                // @ts-expect-error - docx-parser is an optional dependency
                const { readFileAsText } = await import('docx-parser');
                return await readFileAsText(file);
            } catch (e) {
                throw new Error('DOCX support requires the "docx-parser" library. Install with: npm install docx-parser');
            }
        }

        if (ext === 'pdf') {
            try {
                // @ts-expect-error - pdfjs-dist is an optional dependency
                const { getDocument } = await import('pdfjs-dist');
                const arrayBuffer = await file.arrayBuffer();
                const pdf = await getDocument({ data: arrayBuffer }).promise;
                let text = '';
                for (let i = 1; i <= pdf.numPages; i++) {
                    const page = await pdf.getPage(i);
                    const content = await page.getTextContent();
                    text += content.items.map((item: any) => item.str).join('') + '\n';
                }
                return text;
            } catch (e) {
                throw new Error('PDF support requires the "pdfjs-dist" library. Install with: npm install pdfjs-dist');
            }
        }

        throw new Error(`Unsupported file type: .${ext}. Supported formats: .txt, .md, .docx (with docx-parser), .pdf (with pdfjs-dist)`);
    };

    // CRA env var
    const apiBase = useMemo(() => {
        const raw = process.env.REACT_APP_BLOGPOSTS_API || '';
        return raw.trim().replace(/\/$/, '');
    }, []);

    const aiApiBase = useMemo(() => {
        const raw = process.env.REACT_APP_GENERATEBLOG_API || '';
        return raw.trim().replace(/\/$/, '');
    }, []);

    const [formData, setFormData] = useState<BlogFormData>({
        title: '',
        description: '',
        category: '',
        body: '',
        imageKey: '',
        active: true
    });


    // Load all blog posts on mount
    useEffect(() => {
        loadBlogs();
    }, []);

    const loadBlogs = async () => {
        try {
            setBlogsLoading(true);

            if (!apiBase) {
                console.error('REACT_APP_BLOGPOSTS_API is empty');
                setErrorMessage('BlogPosts API is not configured.');
                return;
            }

            const url = `${apiBase}/blogposts`;
            
            const response = await fetch(url);
            const text = await response.text();
            
            if (!response.ok) throw new Error(`Failed to load jobs (${response.status})`);

           const parsed = text ? JSON.parse(text) : [];
const items = Array.isArray(parsed) ? parsed : (parsed.items ?? []);

const sortedItems = items.sort(
    (a: Blog, b: Blog) =>
        new Date(b.pubDate).getTime() - new Date(a.pubDate).getTime()
);

setBlogs(sortedItems);
        } catch (error) {
            console.error('Error loading blogs:', error);
            setErrorMessage('Failed to load blog posts. Please try again later.');
            setBlogs([]);
        } finally {
            setBlogsLoading(false);
        }
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({...prev, [name]: value}));
    };

    const handleBooleanToggle = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, checked } = e.target;
        setFormData(prev => ({...prev, [name]: checked}));
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files[0]) setImageFile(e.target.files[0]);
    };

    const handleBodyFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        setErrorMessage('');
        try {
            const extractedText = await extractTextFromFile(file);
            setFormData(prev => ({...prev, body: extractedText}));
            setBodyFile(file);
            setSuccessMessage(`File "${file.name}" loaded. You can further edit the body text below if needed.`);
            setTimeout(() => setSuccessMessage(''), 5000);
        } catch (error: any) {
            setErrorMessage(error.message || `Failed to read file "${file.name}"`);
            setBodyFile(null);
            if (bodyInputRef.current) bodyInputRef.current.value = '';
        }
    };

    const handleGenerateAiArticle = async () => {
        // start of debug code - to be removed
        console.log("aiApiBase value:", aiApiBase);
        // end of debug code
        setAiMessage('');

        if (!aiApiBase) {
            setAiMessage('Missing environment variable: REACT_APP_GENERATEBLOG_API');
            return;
        }

        setAiGenerating(true);

        try {
            const response = await fetch(aiApiBase, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    topic: aiTopic.trim(),
                    category: aiCategory.trim() || 'Technology',
                    customPrompt: aiCustomPrompt.trim(),
                }),
            });

            const raw = await response.text();

            if (!response.ok) {
                let msg = `Failed to generate article (${response.status})`;
                try {
                    const parsed = JSON.parse(raw);
                    if (parsed?.error) msg = parsed.error;
                    if (parsed?.message) msg = parsed.message;
                } catch {}
                throw new Error(msg);
            }

            const parsed: AiArticleResponse = raw ? JSON.parse(raw) : {};

            const generatedTitle = parsed.title || 'Untitled Article';
            const generatedDescription = parsed.description || '';
            const generatedBody = parsed.body || '';
            const generatedCategory = parsed.category || aiCategory || 'Technology';

            setFormData({
                title: generatedTitle,
                description: generatedDescription,
                category: generatedCategory,
                body: generatedBody,
                imageKey: parsed.imageKey || '',
                active: Boolean(parsed.active ?? false),
            });

            setEditingId(parsed.postID || null);
            setEditingPubDate(parsed.pubDate || null);
            setSuccessMessage('AI article generated and saved. Review it below, then update if needed.');
            await loadBlogs();
            window.scrollTo({ top: 0, behavior: 'smooth' });
        } catch (error: any) {
            console.error('Error generating AI article:', error);
            setAiMessage(error.message || 'Failed to generate AI article');
        } finally {
            setAiGenerating(false);
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsSubmitting(true);
        setSuccessMessage('');
        setErrorMessage('');

        try {
            let imagePayload: { image?: string; contentType?: string } = {};

            const fileFromRef = imageInputRef.current?.files?.[0] ?? imageFile;

            if (fileFromRef) {
                console.log("File found:", fileFromRef.name, fileFromRef.type, fileFromRef.size);
                const base64 = await toBase64(fileFromRef);
                console.log("Base64 length:", base64.length);
                imagePayload = {
                    image: base64,
                    contentType: fileFromRef.type,
                };
            } else {
                console.log("No file selected - submitting without image");
            }

            const isEditing = Boolean(editingId);
            
            const payload = {
                ...formData,
                ...imagePayload,
                postID: isEditing ? editingId : `blog-${Date.now()}`,
                ...(isEditing && editingPubDate ? { pubDate: editingPubDate } : {}),
            };

            const url = `${apiBase}/blogposts`;

            const response = await fetchWithAuth(url, {
                method: isEditing ? `PUT` : `POST`,
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(payload)
            });

            const raw = await response.text();

            if (!response.ok) {
                let msg = `Failed to ${isEditing ? 'update' : 'create'} blog post (${response.status})`;
                try {
                    const parsed = JSON.parse(raw);
                    if (parsed?.message) msg = parsed.message;
                } catch {}
                throw new Error(msg);
            }

            // if lambda returned an imageKey, store it in formData for reference
            try {
                const parsed = JSON.parse(raw);
                if (parsed?.imageKey) {
                    console.log('Image uploaded with key:', parsed.imageKey);
                }
            } catch {}

            setSuccessMessage(`Blog post ${isEditing ? 'updated' : 'created'} successfully!`);

            // reset form
            setFormData({
                title: '',
                description: '',
                category: '',
                body: '',
                imageKey: '',
                active: true
            });

            setEditingId(null);

            if (imageInputRef.current) imageInputRef.current.value = '';
            setImageFile(null);
            loadBlogs();

            setTimeout(() => setSuccessMessage(''), 5000);
        } catch (error: any) {
            console.error('Error creating blog post:', error);
            setErrorMessage(error.message || 'Failed to create blog post');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleEdit = (blog: Blog) => {
        setEditingId(blog.postID);
        setEditingPubDate(blog.pubDate);
        setFormData({
            title: blog.title,
            description: blog.description,
            category: blog.category,
            body: blog.body,
            imageKey: blog.imageKey,
            active: blog.active
        });
        if (imageInputRef.current) imageInputRef.current.value = '';
        setImageFile(null);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleCancelEdit = () => {
        setEditingId(null);
        setEditingPubDate(null);
        setFormData({
            title: '',
            description: '',
            category: '',
            body: '',
            imageKey: '',
            active: true
        });
        setImageFile(null);
    };

    const handleDeleteBlog = async (blog: Blog) => {
        const confirmed = window.confirm(`Delete blog post "${blog.title}"? This action cannot be undone.`);
        if (!confirmed) return;

        try {
            const qs = new URLSearchParams({
                postID: blog.postID,
                pubDate: blog.pubDate,
            }).toString();

            const response = await fetchWithAuth(`${apiBase}/blogposts?${qs}`, {
                method: 'DELETE',
            });

            const raw = await response.text();

            if (!response.ok) {
                let msg = `Failed to delete blog post (${response.status})`;
                try {
                    const parsed = JSON.parse(raw);
                    if (parsed?.message) msg = parsed.message;
                } catch {}
                throw new Error(msg);
            }
            setSuccessMessage('Blog post deleted successfully!');
            await loadBlogs();
            setTimeout(() => setSuccessMessage(''), 5000);
        } catch (error: any) {
            console.error('Error deleting blog post:', error);
            setErrorMessage(error.message || 'Failed to delete blog post');
        } finally {
            setDeletingId(null);
        }
    };

    return (
        <div className={styles.adminInsightsPage}>

            <div className={styles.adminContent}>
                <div className={styles.container}>
                    <div className={styles.adminHeader}>
                        <h1><FaBriefcase /> Blog Posts & Insights Management</h1>
                        <button onClick={signOutRedirect} className={styles.logoutBtn}>
                            <FaSignOutAlt /> Logout
                        </button>
                    </div>

                    {successMessage && (
                        <div className={styles.successAlert}>{successMessage}</div>
                    )}

                    {errorMessage && (
                        <div className={styles.errorAlert}>{errorMessage}</div>
                    )}

                    <div className={styles.aiCard}>
                        <h2><FaRobot /> Generate Article with AI</h2>
                        <p className={styles.aiDescription}>
                            Generate a draft article using your Bedrock-backed Lambda, then review or edit it before publishing.
                        </p>

                        <div className={styles.aiGrid}>
                            <div className={styles.formGroup}>
                                <label htmlFor="aiTopic">Topic</label>
                                <input
                                    id="aiTopic"
                                    type="text"
                                    value={aiTopic}
                                    onChange={(e) => setAiTopic(e.target.value)}
                                    placeholder="e.g. AI trends in healthcare. If no topic is set, a random trending tech topic will be used."
                                />
                            </div>

                            <div className={styles.formGroup}>
                                <label htmlFor="aiCategory">Category</label>
                                <input
                                    id="aiCategory"
                                    type="text"
                                    value={aiCategory}
                                    onChange={(e) => setAiCategory(e.target.value)}
                                    placeholder="e.g. Technology"
                                />
                            </div>

                            <div className={styles.formGroupFull}>
                                <label htmlFor="aiCustomPrompt">Custom Prompt</label>
                                <textarea
                                    id="aiCustomPrompt"
                                    value={aiCustomPrompt}
                                    onChange={(e) => setAiCustomPrompt(e.target.value)}
                                    placeholder="Optional. Override the default generation prompt if you have more constraints."
                                />
                            </div>
                        </div>

                        {aiMessage && <div className={styles.aiMessage}>{aiMessage}</div>}

                        <button
                            type="button"
                            className={styles.aiBtn}
                            onClick={handleGenerateAiArticle}
                            disabled={aiGenerating}
                        >
                            {aiGenerating ? 'Generating...' : <><FaRobot /> Generate Article</>}
                        </button>
                    </div>

                    <div className={styles.formCard}>
                        <h2><FaPlus /> {editingId ? 'Edit Blog Post' : 'Create New Blog Post'}</h2>

                        <form onSubmit={handleSubmit}>
                            <div className={styles.formGrid}>
                                <div className={styles.formGroup}>
                                    <label htmlFor="title">Title *</label>
                                    <input
                                        id="title"
                                        type = "text"
                                        name="title"
                                        value={formData.title}
                                        onChange={handleInputChange}
                                        required
                                        placeholder="Enter blog post title"
                                    />
                                </div>

                                <div className={styles.formGroup}>
                                    <label htmlFor="description"> Description *</label>
                                    <textarea
                                        id="description"
                                        name = "description"
                                        value={formData.description}
                                        onChange={handleInputChange}
                                        required
                                        placeholder="Enter a brief excerpt, summary, or description of the blog post"
                                    />
                                </div>

                                <div className={styles.formGroup}>
                                    <label htmlFor="category">Category *</label>
                                    <input
                                        id="category"
                                        type="text"
                                        name="category"
                                        value={formData.category}
                                        onChange={handleInputChange}
                                        required
                                        placeholder="e.g. AI & Automation, ServiceNow, Digital Transformation"
                                    />
                                </div>

                                <div className={styles.formGroup}>
                                    <label htmlFor="body">Body *</label>
                                    <div className={styles.bodyInputToggle}>
                                        <div className={styles.uploadOption}>
                                            <label htmlFor="bodyFile" className={styles.fileLabel}>
                                                Upload File (.txt, .md, .docx, .pdf)
                                            </label>
                                            <input
                                                type="file"
                                                id="bodyFile"
                                                accept=".txt,.md,.text,.docx,.pdf"
                                                onChange={handleBodyFileChange}
                                                ref={bodyInputRef}
                                            />
                                            {bodyFile && (
                                                <p className={styles.fileInfo}>
                                                    Loaded: {bodyFile.name}
                                                </p>
                                            )}
                                        </div>
                                        <p className={styles.divider}>or</p>
                                        <div className={styles.textOption}>
                                            <label htmlFor="body" className={styles.textLabel}>
                                                Type or Paste Text Directly
                                            </label>
                                        </div>
                                    </div>
                                    <textarea
                                        id="body"
                                        name="body"
                                        value={formData.body}
                                        onChange={handleInputChange}
                                        required
                                        placeholder="Enter the full content of the blog post."
                                    />
                                </div>

                                <div className={styles.formGroup}>
                                    <label htmlFor="image">Upload Image or Thumbnail (jpg or png)</label>
                                    <input
                                        type="file"
                                        id="image"
                                        accept="image/png, image/jpeg"
                                        onChange={handleFileChange}
                                        ref={imageInputRef}
                                    />
                                </div>

                                <div className={styles.formGroup}>
                                    <label htmlFor="active" className={styles.checkboxLabel}>
                                        <input
                                            id="active"
                                            type="checkbox"
                                            name="active"
                                            checked={formData.active}
                                            onChange={handleBooleanToggle}
                                        />
                                        <span>Active (Display on public Insights page)</span>
                                    </label>
                                </div>

                                <div className={styles.formActions}>
                                    <button
                                        type="submit"
                                        className={styles.submitBtn}
                                        disabled={isSubmitting}
                                    >
                                        {editingId ? <><FaEdit /> Update Blog Post</> : <><FaPlus /> Create Blog Post</>}
                                    </button>
                                    {editingId && (
                                        <button
                                            type="button"
                                            className={styles.cancelBtn}
                                            onClick={handleCancelEdit}
                                        >
                                            Cancel
                                        </button>
                                    )}
                                </div>
                            </div>
                        </form>
                    </div>

                    {/* Blog Posts Table */}
                    <div className={styles.blogsTableCard}>
                        <h2>Existing Blog Posts</h2>
                        {blogsLoading && <p>Loading blog posts...</p>}
                        {blogs.length == 0 && !blogsLoading && <p>No blog posts found.</p>}

                        <div className={styles.blogsTable}>
                            <table>
                                    <thead>
                                        <tr>
                                            <th>Title</th>
                                            <th>Date</th>
                                            <th>Active</th>
                                            <th>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {blogs.map(blog => (
                                            <tr key={blog.postID}>
                                                <td className={styles.blogTitle}>{blog.title}</td>
                                                <td>{blog.pubDate}</td>
                                                <td>
                                                    <span className={`${styles.activeBadge} ${blog.active ? styles.active : styles.inactive}`}>
                                                        {blog.active ? 'Active' : 'Inactive'}
                                                    </span>
                                                </td>
                                                <td>
                                                    <button
                                                        className={styles.editBtn}
                                                        onClick={() => handleEdit(blog)}
                                                        title = "Edit Blog Post"
                                                    >
                                                        <FaEdit />Edit
                                                    </button>

                                                    <button
                                                        className={styles.deleteBtn}
                                                        onClick={() => handleDeleteBlog(blog)}
                                                        title="Delete Blog Post"
                                                        disabled={deletingId === blog.postID}
                                                    >
                                                        <FaTrash /> {deletingId === blog.postID ? 'Deleting...' : 'Delete'}
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AdminInsightsPage;
